export interface SaveResourceAction {
  fileName: string;
  displayName: string;
  description?: string;
  content: string;
}

export const SAVE_RESOURCE_LANGUAGE = "istari-save-resource";

export const SAVE_RESOURCE_ALLOWED_EXTENSIONS: readonly string[] = [
  "md",
  "txt",
  "csv",
  "json",
  "yaml",
  "yml",
  "xml",
  "py",
  "html",
];

export const SAVE_RESOURCE_MAX_CONTENT_BYTES = 1_000_000;

export const SAVE_RESOURCE_MAX_FILE_NAME_LENGTH = 128;

/**
 * Instructions appended to the chat system prompt so the assistant emits
 * save-file proposals as fenced blocks matching `parseSaveResourceAction`.
 */
export const SAVE_RESOURCE_INSTRUCTIONS = `# Saving Files
When the user explicitly asks you to save, export, or write conversation content to a file, emit a fenced code block with the language \`${SAVE_RESOURCE_LANGUAGE}\` whose body is a single JSON object:

\`\`\`${SAVE_RESOURCE_LANGUAGE}
{"fileName":"trade-study-summary.md","displayName":"Trade Study Summary","description":"Optional one-line description","content":"# Trade Study\\n\\nFull file content here."}
\`\`\`

Rules:
- Only emit this block when the user asks for a file; never emit it unprompted.
- "content" must be the complete, final file content as a JSON-escaped string. Never truncate it or use placeholders.
- Do not repeat the file content outside the block; the block renders as a save card and the user reviews the content there before anything is written.
- If it is ambiguous which content the user wants saved, ask instead of guessing.
- If the file was already saved from this conversation — it appears in Referenced Resources marked "(saved from this conversation)" — and the user asks to save the same content again, do not emit another block. Tell them it is already saved and link it: [file-name.ext](/resources/<resource_id>). Only emit a new block if the content has changed since that save, and say the result will be a separate new file, not an update to the existing one.
- "fileName" must be a plain name with no directories and one of these extensions: ${SAVE_RESOURCE_ALLOWED_EXTENSIONS.join(", ")}. Pick the extension that matches the content.
- Keep "content" under 1 MB. For anything larger, tell the user it exceeds the chat save limit.`;

export function parseSaveResourceAction(code: string): SaveResourceAction | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(code);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  const record = parsed as Record<string, unknown>;

  if (typeof record.fileName !== "string") return null;
  const fileName = record.fileName.trim();
  if (!isValidSaveResourceFileName(fileName)) return null;

  const { content } = record;
  if (typeof content !== "string" || content.length === 0) return null;
  // Cap the encoded size, not the string length: non-ASCII characters expand
  // to multiple UTF-8 bytes, and the byte size is what the upload carries.
  if (new TextEncoder().encode(content).byteLength > SAVE_RESOURCE_MAX_CONTENT_BYTES) {
    return null;
  }

  if (record.displayName !== undefined && typeof record.displayName !== "string") return null;
  if (record.description !== undefined && typeof record.description !== "string") return null;

  const displayName =
    typeof record.displayName === "string" && record.displayName.trim().length > 0
      ? record.displayName.trim()
      : baseNameOf(fileName);
  const description =
    typeof record.description === "string" && record.description.trim().length > 0
      ? record.description.trim()
      : undefined;

  return { fileName, displayName, content, ...(description === undefined ? {} : { description }) };
}

export function isValidSaveResourceFileName(fileName: string): boolean {
  if (fileName.length === 0 || fileName.length > SAVE_RESOURCE_MAX_FILE_NAME_LENGTH) return false;
  const hasPathOrControlChar = [...fileName].some(
    (char) => char === "/" || char === "\\" || char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127,
  );
  if (hasPathOrControlChar) return false;
  const extension = extensionOf(fileName);
  if (!SAVE_RESOURCE_ALLOWED_EXTENSIONS.includes(extension)) return false;
  return baseNameOf(fileName).length > 0;
}

export function extensionOf(fileName: string): string {
  const separator = fileName.lastIndexOf(".");
  return separator === -1 ? "" : fileName.slice(separator + 1).toLowerCase();
}

export function inferMimeType(fileName: string): string {
  return MIME_BY_EXTENSION[extensionOf(fileName)] ?? "text/plain";
}

function baseNameOf(fileName: string): string {
  const separator = fileName.lastIndexOf(".");
  return (separator === -1 ? fileName : fileName.slice(0, separator)).trim();
}

const MIME_BY_EXTENSION: Record<string, string> = {
  md: "text/markdown",
  txt: "text/plain",
  csv: "text/csv",
  json: "application/json",
  yaml: "application/yaml",
  yml: "application/yaml",
  xml: "application/xml",
  py: "text/x-python",
  html: "text/html",
};
